import { AfterContentInit, Component, HostListener, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { IxfiThemeService } from 'header';
import { Subscription, filter, withLatestFrom } from 'rxjs';
import { CoinDetail } from 'src/app/core/models/coin.model';
import { isPlatformBrowser } from '@angular/common';
import {
    widget,
    StudyOverrides,
    WidgetOverrides,
    ResolutionString,
    LoadingScreenOptions,
    IChartingLibraryWidget,
    ChartingLibraryWidgetOptions
} from 'src/assets/library/charting_library';

import { ActivatedRoute } from '@angular/router';
import { TradingViewService } from 'src/app/core/services/trading-view.service';

@Component({
    selector: 'app-trading-view-chart',
    templateUrl: './trading-view-chart.component.html',
    styleUrls: ['./trading-view-chart.component.scss']
})
export class TradingViewChartComponent implements OnInit, AfterContentInit, OnDestroy {
    isPairAvailable = true;

    pairSymbol!: string;
    coinSymbol!: string;

    isDarkMode!: boolean;
    innerWidth = window.innerWidth;
    studyAdded: boolean = true;

    private readonly userId: string = 'public_user_id';
    private readonly defaultStudyCountLimit: number = 10;
    private readonly clientId: string = 'tradingview.com';
    private readonly defaultInterval = '1D' as ResolutionString;
    private readonly chartContainerId: string = 'tv_chart_container';
    private readonly libraryPath: string = '../../../../assets/library/charting_library/';

    private readonly defaultOverrides: Partial<WidgetOverrides> = {
        'paneProperties.background': this.isDarkMode ? '#191c20' : '#ffffff',
        'paneProperties.vertGridProperties.color': this.isDarkMode
            ? 'rgba(42, 46, 57, 0.06)'
            : 'rgba(171, 184, 200, 0.2)',
        'paneProperties.horzGridProperties.color': this.isDarkMode
            ? 'rgba(42, 46, 57, 0.06)'
            : 'rgba(171, 184, 200, 0.2)',
        'scalesProperties.backgroundColor': '#ffffff',
        'scalesProperties.fontSize': 12,
        'scalesProperties.textColor': this.isDarkMode ? '#ffffff' : '#333333',
        'mainSeriesProperties.candleStyle.upColor': this.isDarkMode ? '#73a822' : '#00b33c',
        'mainSeriesProperties.candleStyle.downColor': this.isDarkMode ? '#E4080B' : '#ff4d4d',
        'paneProperties.separatorColor': '#ffffff'
    };

    private readonly defaultStudyOverrides: StudyOverrides = {
        'volume.volume.color.0': '#E4080B',
        'volume.volume.color.1': '#01a308'
    };

    private readonly defaultLoadingScreen: LoadingScreenOptions = {
        backgroundColor: this.isDarkMode ? '#191c20' : '#ffffff',
        foregroundColor: '$sky-blue'
    };

    private _tvWidget?: IChartingLibraryWidget;
    private _widgetOptions?: ChartingLibraryWidgetOptions;

    subscriptions: Subscription[] = [];
    isBrowser!: boolean;

    private route = inject(ActivatedRoute);
    private platformId = inject(PLATFORM_ID);
    private themeService = inject(IxfiThemeService);
    private tradingViewService = inject(TradingViewService);

    ngOnInit(): void {
        this.isBrowser = isPlatformBrowser(this.platformId);

        const coinDetail: CoinDetail = this.route.snapshot.data['coinDetail'];
        this.coinSymbol = coinDetail.coin_symbol.toUpperCase();

        if (this.isBrowser) {
            const theme$ = this.themeService.themeObs;
            const themeSub: Subscription = theme$.subscribe({
                next: response => {
                    this.isDarkMode = response;
                    this.setTradingViewTheme();
                }
            });
            this.subscriptions.push(themeSub);

            const tvChartSub: Subscription = this.tradingViewService
                .fetchCoinPairs(this.coinSymbol)
                .pipe(withLatestFrom(theme$))
                .subscribe({
                    next: _ => {
                        const selectedPair = this.tradingViewService.findMatchingPair(this.coinSymbol);
                        this.isPairAvailable = !!selectedPair;
                        if (selectedPair) {
                            this.pairSymbol = selectedPair.pair_name;
                            this.initTradingViewChart();
                        }
                    }
                });
            this.subscriptions.push(tvChartSub);
        }
    }

    private setTradingViewTheme(): void {
        if (this._tvWidget) {
            this._tvWidget.changeTheme(this.isDarkMode ? 'Dark' : 'Light');
            this._tvWidget.applyOverrides(this.tradingViewService.getThemeOverrides(this.isDarkMode));
            if (this.isDarkMode) {
                this._tvWidget.applyStudiesOverrides(this.defaultStudyOverrides);
                if (this._widgetOptions) this._widgetOptions.loading_screen = { backgroundColor: '#191c20' };
            } else {
                if (this._widgetOptions) this._widgetOptions.loading_screen = { backgroundColor: '#ffffff' };
            }
        }
    }

    ngAfterContentInit() {
        if (this.isBrowser) {
            const tradingViewSocketSub: Subscription = this.tradingViewService.tradingViewSocketObs
                .pipe(filter(response => this.tradingViewService.filterSocketResponse(response)))
                .subscribe({
                    next: response => this.tradingViewService.processSocketResponse(response)
                });
            this.subscriptions.push(tradingViewSocketSub);
        }
    }

    private initTradingViewChart(): void {
        this._widgetOptions = {
            locale: 'en',
            debug: false,
            autosize: true,
            fullscreen: false,
            timezone: 'exchange',
            user_id: this.userId,
            symbol: this.pairSymbol,
            client_id: this.clientId,
            datafeed: this.setupDatafeed(),
            interval: this.defaultInterval,
            library_path: this.libraryPath,
            overrides: this.defaultOverrides,
            container: this.chartContainerId,
            theme: this.isDarkMode ? 'Dark' : 'Light',
            loading_screen: this.defaultLoadingScreen,
            studies_overrides: this.defaultStudyOverrides,
            study_count_limit: this.defaultStudyCountLimit,
            favorites: this.tradingViewService.getFavorites(),
            enabled_features: this.tradingViewService.getFeatures('enabled'),
            disabled_features: this.tradingViewService.getFeatures('disabled')
        };
        this._tvWidget = new widget(this._widgetOptions);
        this._tvWidget.onChartReady(() => {
            this.setTradingViewTheme();
            if (this.innerWidth > 764) {
                this.studyAdded = true;
                this._tvWidget?.activeChart().createStudy('Volume', true);
            }
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize(_event) {
        this.innerWidth = window.innerWidth;
        if (this.innerWidth > 764 && !this.studyAdded) {
            this.studyAdded = true;
            this._tvWidget?.activeChart().createStudy('Volume', true, false);
        } else if (this.innerWidth < 764 && this.studyAdded) {
            this.studyAdded = false;
            this._tvWidget?.activeChart().removeAllStudies();
            this.initTradingViewChart();
        }
    }

    private setupDatafeed(): ChartingLibraryWidgetOptions['datafeed'] {
        const datafeed: ChartingLibraryWidgetOptions['datafeed'] = {
            onReady: callback => setTimeout(() => callback(this.tradingViewService.getDatafeedConfig())),

            searchSymbols: (userInput, exchange, symbolType, onResultReadyCallback) => {
                this.tradingViewService.searchSymbols(userInput, exchange, symbolType, onResultReadyCallback);
            },

            resolveSymbol: (symbolName, onSymbolResolvedCallback, onResolveErrorCallback, extension) => {
                setTimeout(() =>
                    this.tradingViewService.resolveSymbol(
                        symbolName,
                        onSymbolResolvedCallback,
                        onResolveErrorCallback,
                        extension
                    )
                );
            },

            getBars: (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
                this.tradingViewService.getBars(
                    symbolInfo,
                    resolution,
                    periodParams,
                    onHistoryCallback,
                    onErrorCallback
                );
            },

            subscribeBars: (symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) => {
                this.tradingViewService.subscribeBars(
                    symbolInfo,
                    resolution,
                    onRealtimeCallback,
                    subscriberUID,
                    onResetCacheNeededCallback
                );
            },

            unsubscribeBars: subscriberUID => {
                this.tradingViewService.unsubscribeBars(subscriberUID);
            }
        };

        return datafeed;
    }

    ngOnDestroy(): void {
        if (this._tvWidget) {
            this._tvWidget.remove();
            this._tvWidget = undefined;
        }
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
